import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * `idle` — ready to record · `recording` — mic live, drives Robbie's
 * LISTENING ring · `unsupported` — no MediaRecorder/getUserMedia in this
 * browser · `denied` — the user (or the OS) refused mic permission. The two
 * failure states never throw or surface a raw browser error — callers degrade
 * to typing, in Robbie's voice.
 */
export type VoiceRecorderStatus = 'idle' | 'recording' | 'unsupported' | 'denied';

export interface VoiceClip {
  /** Base64 audio, no `data:` URL prefix — what goes to the chat API. */
  base64: string;
  mimeType: string;
  /** Local object URL for instant playback of the clip just recorded. */
  url: string;
  durationMs: number;
  /**
   * The clip's own shape: mic levels sampled while recording, downsampled to
   * a fixed count and normalized 0..1. Stored with the clip so a bubble can
   * draw the waveform of what was actually said instead of decoration.
   */
  peaks: number[];
}

export interface UseVoiceRecorderOptions {
  /** Hard cap on a single recording — the product's ~60s decision. */
  maxDurationMs?: number;
  onRecordingComplete: (clip: VoiceClip) => void;
}

const PREFERRED_MIME_TYPES = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg'];
const LEVEL_BAR_COUNT = 28;
const LEVEL_SAMPLE_MS = 80;
/** Waveform resolution kept with the clip — enough shape to be recognisable, small enough to store. */
const CLIP_PEAK_COUNT = 40;

const pickMimeType = (): string =>
  PREFERRED_MIME_TYPES.find((t) => typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported?.(t)) || '';

const isVoiceCaptureSupported = (): boolean =>
  typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getUserMedia && typeof MediaRecorder !== 'undefined';

const prefersReducedMotion = (): boolean =>
  typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

/**
 * Every mic sample taken during the clip, squeezed into `CLIP_PEAK_COUNT`
 * buckets (loudest sample wins a bucket) and scaled against the clip's own
 * loudest moment, so a quiet recording still draws a readable waveform.
 * A clip with nothing in it stays flat rather than amplifying room noise.
 */
const toClipPeaks = (samples: number[]): number[] => {
  if (samples.length === 0) return [];
  const loudest = Math.max(...samples);
  const scale = loudest > 0.02 ? 1 / loudest : 0;
  const peaks: number[] = [];
  for (let i = 0; i < CLIP_PEAK_COUNT; i++) {
    const from = Math.floor((i * samples.length) / CLIP_PEAK_COUNT);
    const to = Math.max(from + 1, Math.floor(((i + 1) * samples.length) / CLIP_PEAK_COUNT));
    let bucket = 0;
    for (let j = from; j < to && j < samples.length; j++) bucket = Math.max(bucket, samples[j]);
    peaks.push(Math.round(Math.min(1, bucket * scale) * 100) / 100);
  }
  return peaks;
};

/**
 * Mic capture behind the chat input's mic button: MediaRecorder in, base64 +
 * a local playback URL out. No transcript is ever produced — the recording
 * itself is the message. While recording, also exposes real input levels
 * (Web Audio AnalyserNode) and elapsed time so the input row can show live
 * feedback instead of a static placeholder; those same levels are kept with
 * the finished clip as its waveform, so playback draws what was said rather
 * than a placeholder shape.
 */
export const useVoiceRecorder = ({ maxDurationMs = 60000, onRecordingComplete }: UseVoiceRecorderOptions) => {
  const [status, setStatus] = useState<VoiceRecorderStatus>('idle');
  const [levels, setLevels] = useState<number[]>(() => Array(LEVEL_BAR_COUNT).fill(0));
  const [elapsedMs, setElapsedMs] = useState(0);

  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<BlobPart[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const levelIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const elapsedIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const startedAtRef = useRef(0);
  const maxDurationTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const clipSamplesRef = useRef<number[]>([]);
  // `status` lags a click by a render, so a fast double-tap on the mic would
  // otherwise open a second stream while the first is still coming up —
  // leaving one recorder orphaned with its own live mic and cap timer.
  const startingRef = useRef(false);

  const teardown = useCallback(() => {
    if (maxDurationTimeoutRef.current) clearTimeout(maxDurationTimeoutRef.current);
    if (levelIntervalRef.current) clearInterval(levelIntervalRef.current);
    if (elapsedIntervalRef.current) clearInterval(elapsedIntervalRef.current);
    maxDurationTimeoutRef.current = null;
    levelIntervalRef.current = null;
    elapsedIntervalRef.current = null;
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    recorderRef.current = null;
    chunksRef.current = [];
    clipSamplesRef.current = [];
    analyserRef.current = null;
    startingRef.current = false;
    if (audioCtxRef.current && audioCtxRef.current.state !== 'closed') {
      audioCtxRef.current.close().catch(() => undefined);
    }
    audioCtxRef.current = null;
    setLevels(Array(LEVEL_BAR_COUNT).fill(0));
    setElapsedMs(0);
  }, []);

  const stop = useCallback(() => {
    if (recorderRef.current && recorderRef.current.state !== 'inactive') {
      recorderRef.current.stop();
    }
  }, []);

  /**
   * Real mic levels via an AnalyserNode. Sampling always runs — the clip's
   * waveform is built from it — but under reduced motion the live meter is
   * left as a still row instead of being redrawn several times a second.
   */
  const startLevelMetering = useCallback((stream: MediaStream) => {
    const still = prefersReducedMotion();
    if (still) setLevels(Array(LEVEL_BAR_COUNT).fill(0.35));
    const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
    if (!AudioCtx) return;

    const audioCtx: AudioContext = new AudioCtx();
    const source = audioCtx.createMediaStreamSource(stream);
    const analyser = audioCtx.createAnalyser();
    analyser.fftSize = 256;
    source.connect(analyser);
    audioCtxRef.current = audioCtx;
    analyserRef.current = analyser;

    const data = new Uint8Array(analyser.frequencyBinCount);
    levelIntervalRef.current = setInterval(() => {
      analyser.getByteTimeDomainData(data);
      let sumSquares = 0;
      for (let i = 0; i < data.length; i++) {
        const centered = (data[i] - 128) / 128;
        sumSquares += centered * centered;
      }
      const rms = Math.sqrt(sumSquares / data.length);
      clipSamplesRef.current.push(rms);
      if (still) return;
      const normalized = Math.min(1, rms * 4); // gentle amplification for a visible bar range
      setLevels((prev) => [...prev.slice(1), normalized]);
    }, LEVEL_SAMPLE_MS);
  }, []);

  const start = useCallback(async () => {
    if (!isVoiceCaptureSupported()) {
      setStatus('unsupported');
      return;
    }
    if (startingRef.current || recorderRef.current) return;
    startingRef.current = true;

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mimeType = pickMimeType();
      const recorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
      recorderRef.current = recorder;
      chunksRef.current = [];

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) chunksRef.current.push(event.data);
      };

      recorder.onstop = () => {
        // Measured from the wall clock the recording ran on, and clamped to
        // the cap: a stop that lands late (a throttled background tab, say)
        // must not claim the clip is longer than the recorder would allow.
        // There is deliberately NO fallback to the cap when the measurement
        // looks wrong — a clip reporting the cap it never reached is exactly
        // the bug this guards against; an unmeasurable clip reports nothing.
        const measured = Date.now() - startedAtRef.current;
        const durationMs = Number.isFinite(measured) && measured > 0 ? Math.min(measured, maxDurationMs) : 0;
        const peaks = toClipPeaks(clipSamplesRef.current);
        const blob = new Blob(chunksRef.current, { type: recorder.mimeType || mimeType || 'audio/webm' });
        teardown();
        setStatus('idle');

        const reader = new FileReader();
        reader.onloadend = () => {
          const result = typeof reader.result === 'string' ? reader.result : '';
          const base64 = result.substring(result.indexOf(',') + 1);
          onRecordingComplete({ base64, mimeType: blob.type, url: URL.createObjectURL(blob), durationMs, peaks });
        };
        reader.readAsDataURL(blob);
      };

      clipSamplesRef.current = [];
      startedAtRef.current = Date.now();
      recorder.start();
      startingRef.current = false;
      setStatus('recording');
      startLevelMetering(stream);
      elapsedIntervalRef.current = setInterval(
        () => setElapsedMs(Math.min(Date.now() - startedAtRef.current, maxDurationMs)),
        200
      );
      maxDurationTimeoutRef.current = setTimeout(stop, maxDurationMs);
    } catch {
      // Permission denied, no mic device, or a secure-context failure — all
      // read the same to the user: fall back to typing, no error dialog.
      teardown();
      setStatus('denied');
    }
  }, [maxDurationMs, onRecordingComplete, startLevelMetering, stop, teardown]);

  // Keyed off the live recorder, not `status`: the flag a click sees must be
  // the one the recorder is actually in, not last render's copy of it.
  const toggle = useCallback(() => {
    if (recorderRef.current || status === 'recording') {
      stop();
    } else {
      start();
    }
  }, [status, start, stop]);

  useEffect(() => teardown, [teardown]);

  return { status, toggle, levels, elapsedMs, maxDurationMs };
};

export default useVoiceRecorder;
